<template>
  <div class="signin-modal__shell" :class="{ 'signin-modal__shell--plain': hideBranding }" data-testid="signin-modal-wrapper">
    <section class="signin-modal__form" aria-label="Sign in to Zight">
      <div v-if="!hideBranding" class="signin-modal__branding" :data-testid="view === 'signin' ? 'sign-in-modal-branding' : undefined">
        <ImageSvg name="general.logoWithText" />
      </div>

      <!-- Forgot password form -->
      <template v-if="view === 'forgot'">
        <TitleSeparator class="signin-modal__divider">Forgot password?</TitleSeparator>
        <p class="signin-modal__helper-text">You can reset your password here.</p>
        <Form @submit="onForgotSubmit" :validation-schema="forgotSchema">
          <fieldset class="signin-modal__fields">
            <div class="signin-modal__field">
              <label htmlFor="forgotEmailInput" class="signin-modal__label">Email</label>
              <Field type="text" name="email" id="forgotEmailInput" class="signin-modal__input" />
              <ErrorMessage as="p" class="signin-modal__error" name="email" />
            </div>
            <Button
              class="!w-full signin-modal__submit"
              :loading="forgotInProgress && forgotAction === 'reset'"
              :disabled="forgotInProgress"
              type="solid"
              variant="blue"
              :submit="true"
              @click="forgotAction = 'reset'"
            >
              Reset password
            </Button>
            <TitleSeparator class="signin-modal__divider signin-modal__divider--compact">Or</TitleSeparator>
            <Button
              class="!w-full signin-modal__submit"
              :loading="forgotInProgress && forgotAction === 'magic'"
              :disabled="forgotInProgress"
              type="ghost"
              variant="blue"
              :submit="true"
              @click="forgotAction = 'magic'"
            >
              Send a sign in link via email
            </Button>
          </fieldset>
        </Form>
        <p class="signin-modal__signup">
          <a href="#" @click.stop.prevent="view = 'signin'">Back to Sign in page</a>
        </p>
      </template>

      <!-- Forgot password sent confirmation -->
      <template v-else-if="view === 'sent'">
        <TitleSeparator class="signin-modal__divider">Check your email</TitleSeparator>
        <p class="signin-modal__helper-text">{{ sentMessage }}</p>
        <p class="signin-modal__signup">
          <a href="#" @click.stop.prevent="view = 'signin'">Back to sign in</a>
        </p>
      </template>

      <!-- Sign in form -->
      <template v-else>
        <TitleSeparator class="signin-modal__divider">Sign in with</TitleSeparator>
        <SocialAuth :redirect-url="props.redirectUrl" @action="onSocialAction" :in-progress="socialInProgress" />
        <TitleSeparator class="signin-modal__divider signin-modal__divider--compact">Or</TitleSeparator>

        <Form @submit="onSubmit" :validation-schema="schema">
          <fieldset class="signin-modal__fields">
            <div class="signin-modal__field">
              <label htmlFor="emailInput" class="signin-modal__label">Email</label>
              <Field type="text" name="email" id="emailInput" data-testid="sign-in-email" class="signin-modal__input" />
              <ErrorMessage as="p" class="signin-modal__error" name="email" />
            </div>
            <div class="signin-modal__field">
              <label htmlFor="passwordInput" class="signin-modal__label">
                Password
                <a href="#" @click.stop.prevent="view = 'forgot'" data-testid="sign-in-forgot-password" class="signin-modal__forgot">Forgot password?</a>
              </label>
              <Field type="password" name="password" id="passwordInput" data-testid="sign-in-password" class="signin-modal__input" />
              <ErrorMessage as="p" class="signin-modal__error" name="password" />
            </div>
            <Button
              class="!w-full signin-modal__submit"
              data-testid="sign-in"
              :loading="requestInProgress"
              :disabled="requestInProgress"
              type="solid"
              variant="blue"
              :submit="true"
            >
              Sign in
            </Button>
          </fieldset>
        </Form>

        <p v-if="!hideBranding" class="signin-modal__signup" data-testid="sign-in-modal-signup-link">
          New to Zight?
          <a href="#" @mousedown.prevent @click.stop.prevent="showSignUpModal({ redirectUrl: redirectUrl })" data-testid="sign-up-show-modal"
            >Sign up for free</a
          >
        </p>
      </template>
    </section>

    <aside v-if="!hideBranding" class="signin-modal__promo" data-testid="signin-promo-box" aria-label="Zight Agents benefits">
      <h2 class="signin-modal__promo-title">Record it once.<br />Let the agent do it forever.</h2>
      <p class="signin-modal__promo-subtitle">With Zight Agents, you can:</p>
      <ul role="list" class="signin-modal__promo-list">
        <li><Icon name="general.checkedCircleSolid" /><span>Turn any recording into a reusable AI agent</span></li>
        <li><Icon name="general.checkedCircleSolid" /><span>Automate repetitive workflows without code</span></li>
        <li><Icon name="general.checkedCircleSolid" /><span>Share one link—no more repeat explanations</span></li>
        <li><Icon name="general.checkedCircleSolid" /><span>Onboard teammates faster with step-by-step agents</span></li>
        <li><Icon name="general.checkedCircleSolid" /><span>Works instantly in your browser—no setup required</span></li>
      </ul>
      <a
        href="https://zight.com/agent/?utm_source=product&utm_medium=web&utm_campaign=signup_login_agent_promo&utm_content=see_how_it_works"
        target="_blank"
        rel="noopener noreferrer"
        class="signin-modal__promo-cta"
      >
        See how it works
      </a>
      <img :src="agentsPreview" alt="" aria-hidden="true" class="signin-modal__promo-image" />
    </aside>
  </div>
</template>
<script setup lang="ts">
import { object, string } from 'yup';
import { Form, Field, ErrorMessage } from 'vee-validate';
import { useUserStore } from '@/store/useUserStore';
import SocialAuth from './auth_dialog/Social.vue';
import { useAuth } from '@/composables/useAuth';
import TitleSeparator from './auth_dialog/TitleSeparator.vue';
import { useToast } from '@/composables/useToast';
import { useAuthModals } from '@/composables/useAuthModals';
import { useBranding } from '@/composables/useBranding';
import { ref } from 'vue';
import { Button, ImageSvg, Icon } from '@/storybook';
import agentsPreview from './sign-up-agents-preview.png';
import { Api } from '@/Api';

const props = defineProps<{
  redirectUrl?: string;
}>();

const userStore = useUserStore();

const { showSignUpModal } = useAuthModals();
const { hideBranding } = useBranding();
const requestInProgress = ref(false);
const forgotInProgress = ref(false);
const forgotAction = ref<'reset' | 'magic'>('reset');
const view = ref<'signin' | 'forgot' | 'sent'>('signin');
const sentMessage = ref('');

const schema = object().shape({
  email: string().email().required(),
  password: string().min(6).required(),
});

const forgotSchema = object().shape({
  email: string().email().required(),
});

const { onSocialAction, inProgress: socialInProgress } = useAuth(props.redirectUrl);

const { error } = useToast();
const emits = defineEmits(['close']);

const onForgotSubmit = async (data: { email: string }) => {
  try {
    forgotInProgress.value = true;
    const url = forgotAction.value === 'magic' ? '/api/v5/magic_links' : '/api/v4/account/forgot_password';
    const response = await Api.post(url, { email: data.email });
    sentMessage.value = response.data?.metadata?.message || 'Check your email for instructions.';
    view.value = 'sent';
  } catch {
    error('Something went wrong. Please try again.');
  } finally {
    forgotInProgress.value = false;
  }
};

const onSubmit = async (data: any) => {
  try {
    requestInProgress.value = true;
    const response = await userStore.signIn(data);

    if (response && props.redirectUrl) {
      window.location.assign(props.redirectUrl);
      return;
    }

    if (response && response.metadata && response.metadata.redirectTo) {
      window.location.assign(response.metadata.redirectTo);
      return;
    }

    await userStore.load();

    requestInProgress.value = false;
    emits('close');
  } catch (err: any) {
    requestInProgress.value = false;
    error(err.message);
  }
};
</script>
<style scoped>
.signin-modal__shell {
  width: min(100vw - 2rem, 55rem);
  display: grid;
  grid-template-columns: minmax(20rem, 1fr) minmax(18rem, 0.9fr);
  gap: 0;
  overflow: hidden;
  border-radius: 1.5rem;
  background: var(--white-copernicus, #fff);
  box-shadow:
    0 1.25rem 3.75rem rgba(15, 23, 42, 0.22),
    0 0 0 1px rgba(148, 163, 184, 0.18);
}

.signin-modal__shell--plain {
  width: min(100vw - 2rem, 28rem);
  grid-template-columns: 1fr;
  display: flex;
  flex-direction: column;
  padding: 3rem 3.75rem 2rem;
  background: var(--white-copernicus, #fff);
  color: var(--zinc-900, #18181b);
}

.signin-modal__form {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 16.5px;
  padding: 3rem 3.75rem;
  background: var(--white-copernicus, #fff);
  color: var(--zinc-900, #18181b);
}

.signin-modal__branding {
  display: flex;
  justify-content: center;
}

.signin-modal__branding :deep(svg),
.signin-modal__branding :deep(img) {
  max-height: 2.75rem;
}

.signin-modal__divider {
  --divider-background: var(--white-copernicus, #fff);
  --divider-stroke: #d8e0ec;
  --divider-color: #64748b;
  margin-block: 0 !important;
}

.signin-modal__divider--compact {
  margin-block: 0.25rem !important;
}

.signin-modal__fields {
  display: flex;
  flex-direction: column;
  gap: 16.5px;
  margin: 0;
  padding: 0;
  border: none;
}

.signin-modal__field {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}

.signin-modal__label {
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: #334155;
  font-size: 0.875rem;
  font-weight: 700;
}

.signin-modal__forgot {
  color: #2563eb;
  font-weight: 400;
  text-decoration: none;
  font-size: 0.875rem;
}

.signin-modal__forgot:hover {
  color: #60a5fa;
}

.signin-modal__input {
  width: 100%;
  height: 34px;
  padding: 0 0.75rem;
  border: 1px solid #cdd5e1;
  border-radius: 0.625rem;
  font-size: 0.875rem;
  background: #ffffff;
  color: #0f172a;
  outline: none;
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease;
}

.signin-modal__input:focus {
  border-color: #2563eb;
  box-shadow: 0 0 0 0.25rem rgba(37, 99, 235, 0.18);
}

.signin-modal__error {
  margin-block-start: 0.25rem;
  color: #64748b;
  font-size: 0.875rem;
}

.signin-modal__submit {
  min-height: 3.25rem;
  border-radius: 100px;
  font-size: 14px !important;
  font-weight: 700 !important;
}

.signin-modal__helper-text {
  color: #475569;
  font-size: 0.875rem;
  margin-block: 0;
}

.signin-modal__signup {
  margin-block: 0;
  color: #475569;
  font-size: 0.875rem;
  text-align: center;
}

.signin-modal__signup a {
  color: #2563eb;
  text-decoration: none;
  border-radius: 0.25rem;
}

.signin-modal__signup a:hover {
  color: #60a5fa;
}

.signin-modal__signup a:focus-visible {
  outline: none;
  box-shadow: 0 0 0 0.25rem rgba(37, 99, 235, 0.22);
}

.signin-modal__promo {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  min-height: 36rem;
  padding: 3.5rem 2.5rem 0;
  color: var(--white-copernicus, #fff);
  background: radial-gradient(ellipse at 80% 10%, rgba(255, 255, 255, 0.12) 0, transparent 50%), #77448d;
  overflow: hidden;
}

.signin-modal__promo > * {
  margin-block: 0;
}

.signin-modal__promo-title {
  color: var(--white-copernicus, #fff);
  font-size: clamp(1.25rem, 2.25vw, 1.625rem);
  font-weight: 700;
  line-height: 1.15;
  letter-spacing: -0.02em;
}

.signin-modal__promo-subtitle {
  color: rgba(255, 255, 255, 0.85);
  font-size: 0.8125rem;
  font-weight: 500;
}

.signin-modal__promo-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0;
  list-style: none;
}

.signin-modal__promo-list li {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8125rem;
  font-weight: 500;
}

.signin-modal__promo-cta {
  display: inline-block;
  align-self: flex-start;
  padding: 0.5625rem 1.25rem;
  border: 1.5px solid rgba(255, 255, 255, 0.75);
  border-radius: 100px;
  background: transparent;
  color: var(--white-copernicus, #fff);
  font-size: 0.875rem;
  font-weight: 600;
  text-decoration: none;
  transition:
    background 0.15s ease,
    color 0.15s ease;
}

.signin-modal__promo-cta:hover {
  background: var(--white-copernicus, #fff);
  color: #43316f;
}

.signin-modal__promo-image {
  margin-block-start: auto;
  width: 100%;
  border-radius: 0.75rem 0.75rem 0 0;
  display: block;
}

@media (max-width: 760px) {
  .signin-modal__shell {
    width: min(100vw - 2.5rem, 28rem);
    margin-block-start: 0.75rem;
    grid-template-columns: 1fr;
    max-height: calc(100vh - 3rem);
    overflow-y: auto;
    border-radius: 1.25rem;
  }

  .signin-modal__form {
    padding: 2rem 1.5rem 1.5rem;
  }

  .signin-modal__promo {
    min-height: auto;
    padding: 1.5rem 1.5rem 0;
    margin: 0 0.75rem 0.75rem;
    border-radius: 1rem;
  }

  .signin-modal__promo-title {
    font-size: 1.75rem;
  }

  .signin-modal__promo-image {
    width: 100%;
  }
}
</style>
